<template>
    <section v-if="isContribution" class="pc-card">
        <div class="pc-header">
            <i class="fal fa-lightbulb-on pc-header-icon"></i>
            <h2 class="pc-header-title">{{ $t('post.page.contribution.title') }}</h2>
        </div>

        <div class="pc-body">
            <!-- Visibilité : décidée par le statut, pas par un bouton -->
            <div class="pc-row">
                <span class="pc-label">{{ $t('post.page.contribution.visibility') }}</span>
                <div class="pc-value">
                    <span class="pc-pill" :class="isPublic ? 'pc-pill--public' : 'pc-pill--draft'">
                        <i :class="isPublic ? 'fal fa-globe-europe' : 'fal fa-eye-slash'"></i>
                        {{ isPublic
                            ? $t('post.page.contribution.on_wall')
                            : $t('post.page.contribution.off_wall') }}
                    </span>
                    <span class="pc-hint">
                        {{ isPublic
                            ? $t('post.page.contribution.on_wall_hint', { status: statusName })
                            : $t('post.page.contribution.off_wall_hint', { status: statusName }) }}
                    </span>
                </div>
            </div>

            <!-- Soutiens : le compteur n'est affiché que si le listing le porte -->
            <div v-if="hasVotes || threshold" class="pc-row">
                <span class="pc-label">{{ $t('post.page.contribution.support') }}</span>
                <div class="pc-value">
                    <div v-if="hasVotes" class="pc-votes">
                        <span class="pc-votes-count">{{ votesTotal }}</span>
                        <span class="pc-votes-unit">
                            {{ $t('post.page.contribution.votes_unit') }}
                            <template v-if="threshold">/ {{ threshold }}</template>
                        </span>
                        <span v-if="thresholdReached" class="pc-pill pc-pill--reached">
                            <i class="fal fa-check"></i>
                            {{ $t('post.page.contribution.threshold_reached') }}
                        </span>
                    </div>
                    <div v-if="hasVotes && threshold" class="pc-gauge" role="presentation">
                        <span class="pc-gauge-fill" :style="{ width: gaugeWidth }"></span>
                    </div>
                    <span v-if="threshold" class="pc-hint">
                        {{ $t('post.page.contribution.threshold_hint', { threshold }) }}
                    </span>
                </div>
            </div>

            <!-- Suites données par la commune -->
            <div v-if="post.estimated_delivery" class="pc-row">
                <span class="pc-label">{{ $t('post.page.contribution.estimated_delivery') }}</span>
                <div class="pc-value">
                    <span class="pc-strong">{{ formatDate(post.estimated_delivery) }}</span>
                </div>
            </div>

            <div v-if="completionImage" class="pc-row">
                <span class="pc-label">{{ $t('post.page.contribution.completed') }}</span>
                <div class="pc-value">
                    <a :href="completionImage.web_url || completionImage.url" target="_blank" rel="noopener">
                        <img
                            class="pc-image"
                            :src="completionImage.thumb_url || completionImage.url"
                            :alt="completionImage.name || $t('post.page.contribution.completed')"
                        />
                    </a>
                </div>
            </div>

            <!-- Dernier motif communiqué au citoyen -->
            <div v-if="lastReason" class="pc-row">
                <span class="pc-label">{{ $t('post.page.contribution.reason') }}</span>
                <div class="pc-value">
                    <blockquote class="pc-reason">{{ lastReason }}</blockquote>
                    <span class="pc-hint">{{ $t('post.page.contribution.reason_hint') }}</span>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapState } from 'vuex'
import moment from 'moment'

export default {
    name: 'PostContribution',
    props: ['post'],
    computed: {
        ...mapState('form', {
            allForms: (state) => state.forms
        }),
        ...mapState('post', {
            history: (state) => state.history
        }),
        // Le formulaire du post ne porte pas toujours le drapeau : on retombe
        // sur la liste des formulaires, chargée par l'en-tête du signalement.
        postForm() {
            if (!this.post || !this.post.form) return null
            if (!Array.isArray(this.allForms)) return this.post.form
            return (
                this.allForms.find((form) => form.id === this.post.form.id) ||
                this.post.form
            )
        },
        isContribution() {
            return !!(this.postForm && this.postForm.contribution)
        },
        /**
         * Statut du post enrichi : celui de la liste des formulaires porte
         * `public` et `vote_threshold`, que la charge du signalement n'inclut
         * pas forcément.
         */
        status() {
            const status = this.post && this.post.status
            const statuses = this.postForm && this.postForm.statuses
            if (!status || !Array.isArray(statuses)) return status || null
            return statuses.find((s) => s.id === status.id) || status
        },
        statusName() {
            return (this.status && this.status.name) || ''
        },
        isPublic() {
            return !!(this.status && this.status.public)
        },
        threshold() {
            const value = this.status && this.status.vote_threshold
            return value ? parseInt(value, 10) : null
        },
        /**
         * Le compteur de soutiens est agrégé par les listings citoyens : il
         * n'est affiché que si la charge du back-office le porte, jamais
         * reconstitué ni demandé au mur public carte par carte.
         */
        hasVotes() {
            return this.votesTotal !== null
        },
        votesTotal() {
            const value = this.post && this.post.votes_total
            return value === null || value === undefined ? null : parseInt(value, 10)
        },
        thresholdReached() {
            if (!this.hasVotes || !this.threshold) return false
            return this.votesTotal >= this.threshold
        },
        gaugeWidth() {
            if (!this.hasVotes || !this.threshold) return '0%'
            const ratio = Math.min(1, this.votesTotal / this.threshold)
            return `${Math.round(ratio * 100)}%`
        },
        completionImage() {
            return (this.post && this.post.completion_image) || null
        },
        /**
         * Dernier motif envoyé au citoyen : le serveur le trace dans
         * l'historique sous forme de libellé (jamais d'identifiant).
         */
        lastReason() {
            if (!Array.isArray(this.history)) return ''
            for (let i = this.history.length - 1; i >= 0; i--) {
                const value = this.history[i] && this.history[i].new_value
                if (value && value.reason) return value.reason
            }
            return ''
        }
    },
    methods: {
        formatDate(date) {
            return moment(date).format('L')
        }
    }
}
</script>

<style scoped>
.pc-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.pc-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 24px;
    border-bottom: 1px solid #f1f5f9;
}

.pc-header-icon {
    color: #0891b2;
}

.pc-header-title {
    margin: 0;
    font-size: 1.05rem;
    line-height: 1.4;
    font-weight: 500;
    color: #0891b2;
}

.pc-body {
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 20px 24px;
}

.pc-row {
    display: grid;
    grid-template-columns: 180px minmax(0, 1fr);
    gap: 16px;
    align-items: start;
}

.pc-label {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #94a3b8;
    padding-top: 3px;
}

.pc-value {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.pc-strong {
    font-size: 0.92rem;
    font-weight: 600;
    color: #1e293b;
}

.pc-hint {
    font-size: 0.78rem;
    line-height: 1.45;
    color: #64748b;
}

.pc-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    align-self: flex-start;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.74rem;
    font-weight: 500;
}

.pc-pill--public {
    background: rgba(8, 145, 178, 0.12);
    color: #0e7490;
}

.pc-pill--draft {
    background: #f1f5f9;
    color: #475569;
}

.pc-pill--reached {
    background: rgba(16, 185, 129, 0.14);
    color: #047857;
}

.pc-votes {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 8px;
}

.pc-votes-count {
    font-size: 1.4rem;
    font-weight: 700;
    line-height: 1;
    color: #0e7490;
}

.pc-votes-unit {
    font-size: 0.82rem;
    color: #64748b;
}

.pc-gauge {
    height: 6px;
    border-radius: 999px;
    background: #e2e8f0;
    overflow: hidden;
}

.pc-gauge-fill {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: #0891b2;
}

.pc-image {
    max-width: 180px;
    max-height: 140px;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
}

.pc-reason {
    margin: 0;
    padding: 8px 12px;
    border-left: 3px solid #0891b2;
    background: #f8fafc;
    border-radius: 0 8px 8px 0;
    font-size: 0.88rem;
    line-height: 1.5;
    color: #334155;
    overflow-wrap: anywhere;
}

@media (max-width: 768px) {
    .pc-body {
        padding: 16px;
    }

    .pc-row {
        grid-template-columns: 1fr;
        gap: 6px;
    }

    .pc-label {
        padding-top: 0;
    }
}
</style>
